import * as React from 'react'
import _ from 'lodash'
import useAppStore from '@/store/useAppStore'
import useGetVaults from '@/services/api/vault/useGetVaults'

export interface FlatVault {
  id: number | string
  name: string
  [key: string]: unknown
}

/**
 * Single source of truth for "which vaults are selected on the dashboard".
 * Shared by the vault popover and the projected balance chart's legend so
 * toggling a vault anywhere stays in sync everywhere.
 */
export default function useVaultSelection() {
  const selectedVaultIds = useAppStore(
    (state) => state.dashboardSelectedVaultIds,
  )
  const setSelectedVaultIds = useAppStore(
    (state) => state.setDashboardSelectedVaultIds,
  )
  const splitByVault = useAppStore((state) => state.dashboardSplitByVault)
  const setSplitByVault = useAppStore(
    (state) => state.setDashboardSplitByVault,
  )

  const { data: vaults } = useGetVaults()
  const vaultsFlat: FlatVault[] = React.useMemo(
    () =>
      _.flatten(
        (vaults ?? []).map((vault) =>
          vault.is_group ? vault.vaults : [vault],
        ),
      ),
    [vaults],
  )

  const isSelected = (id: number | string) =>
    selectedVaultIds === null || selectedVaultIds.includes(id)

  const toggleVault = (id: number | string, checked: boolean) => {
    const current = selectedVaultIds ?? vaultsFlat.map((v) => v.id)
    const next = checked
      ? Array.from(new Set([...current, id]))
      : current.filter((vaultId) => vaultId !== id)
    setSelectedVaultIds(next.length === vaultsFlat.length ? null : next)
  }

  const selectedVaults = vaultsFlat.filter((v) => isSelected(v.id))

  return {
    vaultsFlat,
    selectedVaultIds,
    selectedVaults,
    isSelected,
    toggleVault,
    setSelectedVaultIds,
    splitByVault,
    setSplitByVault,
  }
}
